import React, { useState, useEffect, useMemo } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger
} from './ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from './ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from './ui/select';
import { useAuth } from '../context/AuthContext';
import {
  apiListEmployees, apiCreateEmployee, apiUpdateEmployee,
  apiToggleEmployeeStatus, apiDeleteEmployee, apiResetPassword
} from '../api';
import {
  Plus, Pencil, Trash2, Lock, Search, UserCog, User, Briefcase,
  CheckCircle2, XCircle, Copy, KeyRound
} from 'lucide-react';
import { toast } from 'sonner';

const genTempPassword = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let s = '';
  for (let i = 0; i < 10; i++) s += chars[Math.floor(Math.random() * chars.length)];
  return s;
};

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';
const fmtDateTime = (iso) => iso ? new Date(iso).toLocaleString('it-IT', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'Mai';

const ROLE_OPTIONS = [
  { value: 'employee', label: 'Dipendente' },
  { value: 'hr', label: 'HR Admin' },
  { value: 'erp', label: 'ERP / Lavorazioni' },
  { value: 'production', label: 'Produzione' },
];

const ROLE_LABEL = Object.fromEntries(ROLE_OPTIONS.map(r => [r.value, r.label]));

// Visual style per role (badge + icon). Extracted to keep the row template flat.
const ROLE_BADGE_CLASS = {
  hr: 'bg-indigo-50 text-indigo-800 border border-indigo-200',
  erp: 'bg-blue-50 text-blue-800 border border-blue-200',
  production: 'bg-amber-50 text-amber-800 border border-amber-200',
  employee: 'bg-slate-50 text-slate-800 border border-slate-200',
};

const roleIcon = (role) => {
  if (role === 'hr') return Briefcase;
  if (role === 'employee') return User;
  return UserCog;
};

const EmployeeForm = ({ initial, onSubmit, onCancel }) => {
  const isEdit = !!initial;
  const [form, setForm] = useState({
    email: initial?.email || '',
    name: initial?.name || '',
    role: initial?.role || 'employee',
    erp_access: initial?.erp_access ?? false,
    production_access: initial?.production_access ?? false,
    temp_password: isEdit ? '' : genTempPassword()
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Inserisci il nome completo'); return; }
    if (!isEdit && (!form.email.trim() || !form.email.includes('@'))) { toast.error('Email non valida'); return; }
    if (!isEdit && form.temp_password.length < 6) { toast.error('Password minimo 6 caratteri'); return; }
    onSubmit(form);
  };

  const copyPwd = () => {
    navigator.clipboard.writeText(form.temp_password);
    toast.success('Password copiata negli appunti');
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="employee-form">
      <div className="space-y-2">
        <Label htmlFor="eemail">Email aziendale <span className="text-red-500">*</span></Label>
        <Input
          id="eemail"
          data-testid="employee-email-input"
          type="email"
          placeholder="nome.cognome@azienda.it"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          disabled={isEdit}
          required
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="ename">Nome completo <span className="text-red-500">*</span></Label>
        <Input id="ename" data-testid="employee-name-input" placeholder="Mario Rossi" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
      </div>
      <div className="space-y-2">
        <Label htmlFor="erole">Ruolo principale</Label>
        <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
          <SelectTrigger id="erole" data-testid="employee-role-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {ROLE_OPTIONS.map(r => (
              <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-[11px] text-slate-500">
          Il ruolo principale determina il dashboard predefinito. Usa gli accessi aggiuntivi per abilitare moduli extra.
        </p>
      </div>
      <div className="space-y-2">
        <Label>Accessi aggiuntivi ai moduli</Label>
        <div className="grid grid-cols-2 gap-2">
          <label className="flex items-center gap-2 border rounded-lg px-3 py-2 cursor-pointer hover:bg-slate-50">
            <input
              type="checkbox"
              data-testid="employee-erp-access"
              checked={!!form.erp_access}
              onChange={(e) => setForm({ ...form, erp_access: e.target.checked })}
              className="w-4 h-4"
            />
            <span className="text-sm">Modulo ERP / Lavorazioni</span>
          </label>
          <label className="flex items-center gap-2 border rounded-lg px-3 py-2 cursor-pointer hover:bg-slate-50">
            <input
              type="checkbox"
              data-testid="employee-prod-access"
              checked={!!form.production_access}
              onChange={(e) => setForm({ ...form, production_access: e.target.checked })}
              className="w-4 h-4"
            />
            <span className="text-sm">Modulo Produzione</span>
          </label>
        </div>
      </div>
      {!isEdit && (
        <div className="space-y-2">
          <Label htmlFor="etemp">Password temporanea <span className="text-red-500">*</span></Label>
          <div className="flex gap-2">
            <Input id="etemp" data-testid="employee-temp-password-input" value={form.temp_password} onChange={(e) => setForm({ ...form, temp_password: e.target.value })} className="font-mono" required />
            <Button type="button" variant="outline" size="icon" onClick={() => setForm({ ...form, temp_password: genTempPassword() })} title="Genera nuova password">
              <KeyRound className="w-4 h-4" />
            </Button>
            <Button type="button" variant="outline" size="icon" onClick={copyPwd} title="Copia password">
              <Copy className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-[11px] text-slate-500">Il dipendente dovrà cambiarla al primo accesso. Condividila in modo sicuro.</p>
        </div>
      )}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>Annulla</Button>
        <Button type="submit" data-testid="employee-form-submit" className="bg-blue-700 hover:bg-blue-800">
          {isEdit ? 'Salva modifiche' : 'Crea dipendente'}
        </Button>
      </DialogFooter>
    </form>
  );
};

const EmployeeManager = () => {
  const { user: currentUser } = useAuth();
  const [list, setList] = useState([]);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPwd, setResetPwd] = useState('');

  const reload = () => apiListEmployees().then(setList).catch(() => setList([]));
  useEffect(() => { reload(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    let l = list;
    if (roleFilter !== 'all') l = l.filter(u => u.role === roleFilter);
    if (statusFilter !== 'all') l = l.filter(u => (statusFilter === 'active') === u.active);
    if (search) {
      const q = search.toLowerCase();
      l = l.filter(u => u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q));
    }
    return l;
  }, [list, search, roleFilter, statusFilter]);

  const handleSubmit = async (data) => {
    try {
      if (editing) {
        await apiUpdateEmployee(editing.id, {
          name: data.name,
          role: data.role,
          erp_access: !!data.erp_access,
          production_access: !!data.production_access,
        });
        toast.success('Dipendente aggiornato');
      } else {
        await apiCreateEmployee({
          email: data.email,
          name: data.name,
          role: data.role,
          temp_password: data.temp_password,
          erp_access: !!data.erp_access,
          production_access: !!data.production_access,
        });
        toast.success(`Dipendente creato. Password temporanea: ${data.temp_password}`, { duration: 8000 });
      }
      setDialogOpen(false);
      setEditing(null);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore');
    }
  };

  const toggleStatus = async (u) => {
    try {
      await apiToggleEmployeeStatus(u.id, !u.active);
      toast.success(u.active ? 'Dipendente disattivato' : 'Dipendente riattivato');
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore');
    }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await apiDeleteEmployee(toDelete.id);
      toast.success('Dipendente eliminato definitivamente');
      setToDelete(null);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore');
    }
  };

  const handleResetPassword = async () => {
    if (!resetTarget || resetPwd.length < 6) { toast.error('Password minimo 6 caratteri'); return; }
    try {
      await apiResetPassword(resetTarget.id, resetPwd);
      toast.success(`Password reimpostata. Nuova password: ${resetPwd}`, { duration: 8000 });
      setResetTarget(null);
      setResetPwd('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col lg:flex-row gap-2 justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Cerca nome o email..." value={search} onChange={(e) => setSearch(e.target.value)} className="h-9 pl-8 w-[220px] text-sm" />
          </div>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="h-9 w-[180px] text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i ruoli</SelectItem>
              <SelectItem value="employee">Dipendenti</SelectItem>
              <SelectItem value="hr">HR Admin</SelectItem>
              <SelectItem value="erp">ERP / Lavorazioni</SelectItem>
              <SelectItem value="production">Produzione</SelectItem>
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-9 w-[160px] text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli stati</SelectItem>
              <SelectItem value="active">Attivi</SelectItem>
              <SelectItem value="inactive">Disattivati</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) setEditing(null); }}>
          <DialogTrigger asChild>
            <Button onClick={() => { setEditing(null); setDialogOpen(true); }} className="bg-blue-700 hover:bg-blue-800 gap-2">
              <Plus className="w-4 h-4" /> Nuovo dipendente
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editing ? 'Modifica dipendente' : 'Nuovo dipendente'}</DialogTitle>
              <DialogDescription>
                {editing ? 'Aggiorna nome e ruolo. L\'email non è modificabile.' : 'Crea un nuovo account. Il dipendente dovrà cambiare la password al primo accesso.'}
              </DialogDescription>
            </DialogHeader>
            <EmployeeForm initial={editing} onSubmit={handleSubmit} onCancel={() => { setDialogOpen(false); setEditing(null); }} />
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase text-slate-600">Dipendente</th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase text-slate-600">Ruolo</th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase text-slate-600">Stato</th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase text-slate-600">Creato il</th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase text-slate-600">Ultimo accesso</th>
              <th className="text-right px-4 py-2.5 text-xs font-semibold uppercase text-slate-600">Azioni</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500 text-sm">Nessun dipendente trovato</td></tr>
            ) : (
              filtered.map(u => (
                <tr key={u.id} className={`hover:bg-slate-50 ${!u.active ? 'opacity-60' : ''}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-600 to-blue-800 text-white flex items-center justify-center text-xs font-semibold">
                        {u.name.split(' ').map(x => x[0]).slice(0, 2).join('').toUpperCase()}
                      </div>
                      <div>
                        <div className="font-medium text-slate-900">{u.name}</div>
                        <div className="text-xs text-slate-500">{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap items-center gap-1">
                      {(() => {
                        const RoleIcon = roleIcon(u.role);
                        const badgeClass = ROLE_BADGE_CLASS[u.role] || ROLE_BADGE_CLASS.employee;
                        return (
                          <div className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full ${badgeClass}`}>
                            <RoleIcon className="w-3 h-3" />
                            {ROLE_LABEL[u.role] || u.role}
                          </div>
                        );
                      })()}
                      {u.is_admin && (
                        <div className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-purple-50 text-purple-800 border border-purple-200 font-semibold">
                          ADMIN
                        </div>
                      )}
                      {u.erp_access && u.role !== 'erp' && (
                        <div className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                          +ERP
                        </div>
                      )}
                      {u.production_access && u.role !== 'production' && (
                        <div className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                          +PROD
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {u.active ? (
                      <div className="inline-flex items-center gap-1 text-xs text-emerald-800"><CheckCircle2 className="w-3.5 h-3.5" />Attivo</div>
                    ) : (
                      <div className="inline-flex items-center gap-1 text-xs text-red-700"><XCircle className="w-3.5 h-3.5" />Disattivato</div>
                    )}
                    {u.must_change_password && (
                      <div className="inline-flex items-center gap-1 text-[10px] text-amber-800 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-full ml-1 mt-0.5">
                        <Lock className="w-2.5 h-2.5" />1° accesso
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">{fmtDate(u.created_at)}</td>
                  <td className="px-4 py-3 text-xs text-slate-600">{fmtDateTime(u.last_login)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => { setEditing(u); setDialogOpen(true); }} title="Modifica ruolo e accessi">
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => { setResetTarget(u); setResetPwd(genTempPassword()); }} title="Reset password">
                        <KeyRound className="w-3.5 h-3.5" />
                      </Button>
                      {u.id !== currentUser?.id && (
                        <>
                          <Button size="sm" variant="ghost" className="h-8 px-2 text-xs" onClick={() => toggleStatus(u)}>
                            {u.active ? 'Disattiva' : 'Attiva'}
                          </Button>
                          <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-600 hover:bg-red-50" onClick={() => setToDelete(u)} title="Elimina">
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Delete dialog */}
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare definitivamente {toDelete?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Verranno eliminati anche: chat, richieste ferie, protocolli malattia, timbrature e buste paga del dipendente. <strong>Azione irreversibile.</strong>
              <br/><br/>Se vuoi solo bloccare l'accesso conservando lo storico, usa invece "Disattiva".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Elimina tutto</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset password dialog */}
      <Dialog open={!!resetTarget} onOpenChange={(o) => !o && setResetTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset password di {resetTarget?.name}</DialogTitle>
            <DialogDescription>
              Imposta una nuova password temporanea. Il dipendente dovrà cambiarla al prossimo accesso.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <div className="flex gap-2">
              <Input value={resetPwd} onChange={(e) => setResetPwd(e.target.value)} className="font-mono" />
              <Button type="button" variant="outline" size="icon" onClick={() => setResetPwd(genTempPassword())}>
                <KeyRound className="w-4 h-4" />
              </Button>
              <Button type="button" variant="outline" size="icon" onClick={() => { navigator.clipboard.writeText(resetPwd); toast.success('Copiata'); }}>
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetTarget(null)}>Annulla</Button>
            <Button onClick={handleResetPassword} className="bg-blue-700 hover:bg-blue-800">Reimposta password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmployeeManager;
